import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { MetadataStoreService } from '../services/metadata-store.service';
import { firstValueFrom } from 'rxjs';
import { filter, timeout } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class UserRouteGuard implements CanActivate {
  constructor(
    private metadataStore: MetadataStoreService,
    private router: Router
  ) {}

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean> {
    // Check if this is a /users route
    if (!state.url.startsWith('/users')) {
      // Not a user route, allow navigation
      return true;
    }

    // Get metadata to check user principals
    // AuthGuard runs first and stores metadata, so it should be available
    let metadata = this.metadataStore.getMetadata();
    
    // If metadata is not immediately available, wait briefly for it
    if (!metadata) {
      try {
        // Wait for metadata to be available (with 1 second timeout)
        metadata = await firstValueFrom(
          this.metadataStore.metadata$.pipe(
            filter(m => m !== null),
            timeout(1000)
          )
        );
      } catch (error) {
        // Timeout or error - allow navigation (AuthGuard will handle auth)
        console.warn('UserRouteGuard: Metadata not available, allowing navigation');
        return true;
      }
    }

    if (!metadata) {
      // Still no metadata, allow navigation
      return true;
    }

    // Get user principals from metadata
    const userPrincipals = metadata?.user?.principals || [];
    const principalsArray = Array.isArray(userPrincipals) ? userPrincipals : [];

    // Check if user has 'customer' principal
    if (principalsArray.includes('customer')) {
      console.log('UserRouteGuard: Customer user attempted to access user management, redirecting to FID picker');
      // Redirect customer users to FID picker
      this.router.navigate(['/fids']);
      return false;
    }

    // User is not a customer (likely support), allow navigation
    return true;
  }
}
